import { useEffect, useRef, useState } from "react";
import { Box, Typography, Skeleton, Chip, IconButton, Collapse } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { parseISO, format, differenceInMinutes } from "date-fns";
import { fetchConversationMessages } from "../../api/conversations";
import type { ConversationSummary, ConversationMessage } from "../../api/conversations";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CHANNEL_COLOURS: Record<string, string> = {
  web_widget: "#C9734F", facebook: "#3B82F6", instagram: "#A855F7",
  sms: "#D4A63D", voice: "#22C55E", whatsapp: "#22C55E",
};

function fmtDuration(start: string, end: string | null) {
  try {
    const mins = differenceInMinutes(parseISO(end || new Date().toISOString()), parseISO(start));
    if (mins < 1) return "< 1 min";
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  } catch { return ""; }
}

function fmtTime(iso: string) {
  try { return format(parseISO(iso), "h:mm a"); } catch { return ""; }
}

function fmtDate(iso: string) {
  try { return format(parseISO(iso), "d MMM yyyy"); } catch { return ""; }
}

function gapMinutes(a: ConversationMessage, b: ConversationMessage) {
  try {
    return Math.abs(differenceInMinutes(parseISO(b.timestamp), parseISO(a.timestamp)));
  } catch { return 0; }
}

function primaryName(c: ConversationSummary) {
  return c.display?.primary_name || c.customer_name;
}

function hasIdentityMismatch(c: ConversationSummary) {
  return (
    c.identity_resolution_status === "mismatch_flagged" ||
    Boolean(c.mismatch_flags?.length)
  );
}

function matchedContactLabel(c: ConversationSummary) {
  const contactName = c.matched_contact?.display_name;
  const currentName = primaryName(c);
  if (!contactName || contactName === currentName) return null;
  return `Matched contact: ${contactName}`;
}

// ─── Outcome chip ─────────────────────────────────────────────────────────────

function OutcomeChip({ outcome }: { outcome: string | null }) {
  if (!outcome) return null;
  const cfg: Record<string, { color: string; bg: string }> = {
    booked:    { color: "#16A34A", bg: "#F0FDF4" },
    escalated: { color: "#D97706", bg: "#FFFBEB" },
    lead:      { color: "#3B82F6", bg: "#EFF6FF" },
  };
  const c = cfg[outcome] ?? { color: "#64748B", bg: "#F8FAFC" };
  return (
    <Chip
      label={outcome.charAt(0).toUpperCase() + outcome.slice(1)}
      size="small"
      sx={{ fontSize: "0.72rem", fontWeight: 700, color: c.color, backgroundColor: c.bg, height: 22 }}
    />
  );
}

// ─── Sentiment chip ───────────────────────────────────────────────────────────

const SENTIMENT_COLOURS_CHIP: Record<string, string> = {
  positive: "#16A34A",
  neutral:  "#64748B",
  negative: "#EF4444",
  mixed:    "#D97706",
};

const RISK_FLAG_LABELS_CHIP: Record<string, string> = {
  complaint:          "Complaint",
  angry_customer:     "Angry Customer",
  cancellation_risk:  "Cancellation Risk",
  pricing_confusion:  "Pricing Confusion",
  booking_friction:   "Booking Friction",
  availability_issue: "Availability Issue",
  service_confusion:  "Service Confusion",
  handoff_needed:     "Handoff Needed",
  repeat_contact:     "Repeat Contact",
  lead_capture:       "Lead Capture",
};

function SentimentChip({ label }: { label: string | null }) {
  if (!label) return null;
  const color = SENTIMENT_COLOURS_CHIP[label.toLowerCase()] ?? "#64748B";
  return (
    <Chip label={label} size="small" sx={{ fontSize: "0.72rem", fontWeight: 600, color, backgroundColor: `${color}15`, height: 22, textTransform: "capitalize" }} />
  );
}

// ─── Chat bubble ─────────────────────────────────────────────────────────────

function Bubble({ msg }: { msg: ConversationMessage }) {
  const isUser = msg.role === "user";
  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", mb: 0.8 }}>
      <Box
        sx={{
          maxWidth: "72%",
          px: 1.5, py: 1,
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          backgroundColor: isUser ? "#0F172A" : "#fff",
          border: isUser ? "none" : "1px solid #E2E8F0",
          boxShadow: isUser ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        <Typography sx={{ fontSize: "0.85rem", color: isUser ? "#F1F5F9" : "#1E293B", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>
          {msg.content}
        </Typography>
        <Typography sx={{ fontSize: "0.65rem", color: isUser ? "rgba(255,255,255,0.4)" : "#CBD5E1", mt: 0.3, textAlign: isUser ? "right" : "left" }}>
          {fmtTime(msg.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Time divider ─────────────────────────────────────────────────────────────

function TimeDivider({ label }: { label: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 1.5 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#F1F5F9" }} />
      <Typography sx={{ fontSize: "0.7rem", color: "#CBD5E1" }}>{label}</Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#F1F5F9" }} />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  conversation: ConversationSummary;
  onClose: () => void;
}

export default function ConversationTranscript({ conversation: conv, onClose }: Props) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    fetchConversationMessages(conv.analytics_conversation_id)
      .then((r) => setMessages(r.messages))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [conv.analytics_conversation_id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const channelColour = CHANNEL_COLOURS[conv.channel] ?? "#64748B";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#F8FAFC" }}>
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.5, backgroundColor: "#fff", borderBottom: "1px solid #E2E8F0", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: channelColour, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {primaryName(conv)}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.72rem", color: "#64748B" }}>
                {conv.channel_label} · {fmtDate(conv.started_at)} · {fmtDuration(conv.started_at, conv.ended_at)} · {conv.message_count} messages
              </Typography>
              {matchedContactLabel(conv) && (
                <Typography sx={{ fontSize: "0.72rem", color: "#64748B" }}>
                  {matchedContactLabel(conv)}
                </Typography>
              )}
              {hasIdentityMismatch(conv) && (
                <Chip
                  label="Name mismatch"
                  size="small"
                  sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, color: "#B45309", backgroundColor: "#FEF3C7" }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <OutcomeChip outcome={conv.outcome} />
            <SentimentChip label={conv.sentiment_status === "completed" ? conv.sentiment_label : null} />
            <IconButton size="small" onClick={onClose} sx={{ color: "#94A3B8", "&:hover": { color: "#0F172A" } }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Sentiment summary card */}
      {conv.sentiment_status === "completed" && (
        <Box sx={{ mx: 2, mt: 1.5, backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", overflow: "hidden", flexShrink: 0 }}>
          <Box
            onClick={() => setSummaryOpen((v) => !v)}
            sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", "&:hover": { backgroundColor: "#F8FAFC" } }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#64748B" }}>AI Summary</Typography>
              {conv.sentiment_confidence != null && (
                <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8" }}>
                  {Math.round(conv.sentiment_confidence * 100)}% confidence
                </Typography>
              )}
              {conv.sentiment_score != null && (
                <Typography sx={{ fontSize: "0.68rem", color: "#94A3B8" }}>
                  score {conv.sentiment_score > 0 ? "+" : ""}{conv.sentiment_score.toFixed(2)}
                </Typography>
              )}
            </Box>
            <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: "#94A3B8", transform: summaryOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </Box>
          <Collapse in={summaryOpen}>
            {conv.sentiment_summary && (
              <Typography sx={{ px: 2, pt: 0.5, pb: conv.sentiment_risk_flags?.length ? 0.5 : 1.5, fontSize: "0.82rem", color: "#1E293B", lineHeight: 1.6 }}>
                {conv.sentiment_summary}
              </Typography>
            )}
            {conv.sentiment_risk_flags && conv.sentiment_risk_flags.length > 0 && (
              <Box sx={{ px: 2, pb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {conv.sentiment_risk_flags.map((flag) => (
                  <Chip
                    key={flag}
                    label={RISK_FLAG_LABELS_CHIP[flag] ?? flag}
                    size="small"
                    sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#EF4444", backgroundColor: "#FEF2F2", height: 20 }}
                  />
                ))}
              </Box>
            )}
          </Collapse>
        </Box>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-end" : "flex-start", mb: 1 }}>
              <Skeleton variant="rounded" width={240} height={52} sx={{ borderRadius: "12px" }} />
            </Box>
          ))
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: "center", pt: 6 }}>
            <Typography sx={{ fontSize: "0.85rem", color: "#94A3B8" }}>No messages found for this session window.</Typography>
          </Box>
        ) : (
          <>
            {messages.map((msg, i) => (
              <Box key={msg.message_id}>
                {i > 0 && gapMinutes(messages[i - 1], msg) >= 5 && (
                  <TimeDivider label={fmtTime(msg.timestamp)} />
                )}
                <Bubble msg={msg} />
              </Box>
            ))}
          </>
        )}
        <div ref={bottomRef} />
      </Box>
    </Box>
  );
}
